import { supabaseClient } from "../auth/supabase.service.js";
import { holdButton } from "./ui-library.js";
import { runWaffleSyncACL } from "./waffle-helpers.js";

/**
 * Reusable Access Control Manager
 * 
 * Manages access control lists for different scopes (server, dataset, site, page)
 * Can be used in dialogs, standalone views, or grouped views
 * 
 * @param {Object} config
 * @param {Object} config.container - D3 selection for the container
 * @param {Object} config.message - Message utilities {bad, info, progress, good}
 * @param {string} config.scope - Scope type: 'server', 'dataset', 'site', 'page'
 * @param {string} config.resource - Resource identifier
 * @param {string} [config.selectedServerId] - Server URL for waffle sync (needed for server/dataset scopes)
 * @param {boolean} [config.showAddButton=true] - Show "Grant access" button
 * @param {boolean} [config.showExplanations=true] - Show explanations in add form
 * @param {Function} [config.onUpdate] - Callback after access changes
 * @param {Array} [config.columns] - Column definitions: [{field, label, width, render}]
 * @param {string} [config.tableClass] - CSS class for table styling
 */
export class AccessControlManager {
  constructor(config) {
    this.container = config.container;
    this.message = config.message;
    this.scope = config.scope;
    this.resource = config.resource;
    this.selectedServerId = config.selectedServerId;
    this.showAddButton = config.showAddButton !== false;
    this.showExplanations = config.showExplanations !== false;
    this.onUpdate = config.onUpdate;
    this.tableClass = config.tableClass || 'admin-table';
    
    // Default columns: email, parent, level, actions
    this.columns = config.columns || [
      { field: 'email', label: 'Email' },
      { field: 'parent', label: 'Granted by', render: (d) => d || '—' },
      { field: 'level', label: 'Level', editable: true },
      { field: 'actions', label: 'Revoke access' }
    ];
    
    this.data = [];
  }

  getExplanation(level) {
    const explanations = {
      server: {
        reader: "👀 Reader can list public datasets on server, for private datasets need special access",
        editor: "✏️ Editor can also sync, delete, add and remove branches, add new datasets on server, for private datasets need special access",
        owner: "👑 Owner has total control of all datasets on server, including the private ones"
      },
      dataset: {
        reader: "👀 Reader can see the private dataset in graphs, create and share permalinks",
        editor: "✏️ Editor can also sync, delete, add and remove branches of the private dataset on server. Server EDIT access is also needed.",
        owner: "👑 Owner can also manage access for other users"
      },
      site: {
        reader: "👀 Reader can view the site configuration",
        editor: "✏️ Editor can modify site settings and content",
        owner: "👑 Owner can manage access and delete the site"
      },
      page: {
        reader: "👀 Reader can view the page configuration",
        editor: "✏️ Editor can modify page settings and content",
        owner: "👑 Owner can manage access and delete the page"
      }
    };

    return explanations[this.scope]?.[level] || "";
  }

  emojifyLevel(level) {
    const emoji = level === "reader" ? "👀" : level === "editor" ? "✏️" : level === "owner" ? "👑" : "";
    return `${emoji} ${level}`;
  }

  async load() {
    const { data, error } = await supabaseClient.rpc('list_acl', { 
      _scope: this.scope, 
      _resource: this.resource 
    });

    if (error) {
      console.error(error);
      if (this.message) this.message.bad(error.message || 'Failed to load access list');
      return { error };
    }

    this.data = data || [];
    return { data: this.data };
  }

  async render() {
    const { error } = await this.load();
    if (error) return;

    this.container.html('');
    
    const table = this.container.append("table").attr("class", this.tableClass);
    
    this.renderTableHeader(table);
    this.renderTableRows(table);
    
    if (this.showAddButton) {
      this.renderAddButton();
    }
  }

  renderTableHeader(table) {
    const headerRow = table.append("tr");
    
    this.columns.forEach(col => {
      headerRow.append("th").text(col.label);
    });
  }

  renderTableRows(table) {
    const self = this;
    
    table.selectAll("tr.item")
      .data(this.data)
      .enter().append("tr")
      .attr("class", "item")
      .each(function(row) {
        const rowEl = d3.select(this);
        
        self.columns.forEach(col => {
          if (col.field === 'level' && col.editable) {
            // Editable level cell
            const levelCell = rowEl.append("td").attr("class", "editable");
            levelCell.append("span").attr("class", "static").text(self.emojifyLevel(row.level));
            
            levelCell.on("click", function(event) {
              if (event) event.stopPropagation();
              self.editLevel(d3.select(this), row);
            });
          } else if (col.field === 'actions') {
            // Actions cell with delete button
            const actionsCell = rowEl.append("td");
            const buttonDelete = actionsCell.append("button")
              .text("✘")
              .attr("class", "button delete hold-btn");

            holdButton(buttonDelete, async () => {
              await self.revokeAccess(row);
            });
          } else {
            // Regular data cell
            const value = col.render ? col.render(row[col.field], row) : row[col.field];
            rowEl.append("td").text(value || '');
          }
        });
      });
  }

  editLevel(cell, row) {
    const self = this;
    
    // Prevent re-entering edit if already editing
    if (cell.classed("editing")) return;
    
    cell.classed("editing", true);
    cell.selectAll("*").remove();
    
    // Wrap editing controls in a div to avoid flexbox on td
    const wrapper = cell.append("div");
    
    const roles = ['reader', 'editor', 'owner'];
    const select = wrapper.append("select")
      .attr("class", "input")
      .style("width", "50%")
      .on("click", (e) => { if (e) e.stopPropagation(); });
    
    roles.forEach(r => {
      select.append("option")
        .attr("value", r)
        .property("selected", r === row.level)
        .text(self.emojifyLevel(r));
    });
    
    const actions = wrapper.append("span")
      .attr("class", "actionsbox")
      .on("click", (e) => { if (e) e.stopPropagation(); });
    
    const saveBtn = actions.append("button").attr("class", "button addnew").text("Save");
    const cancelBtn = actions.append("button").attr("class", "button delete").text("Cancel");

    const resetView = () => {
      cell.classed("editing", false).selectAll("*").remove();
      cell.append("span").attr("class", "static").text(self.emojifyLevel(row.level));
    };

    cancelBtn.on("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      resetView();
    });

    saveBtn.on("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const newLevel = select.property("value");
      if (newLevel === row.level) {
        resetView();
        return;
      }

      if (self.message) self.message.progress('Saving…');
      
      // Use row's scope/resource if available (for multi-resource tables)
      const scope = row.scope || self.scope;
      const resource = row.resource || self.resource;
      const selectedServerId = row.selectedServerId || this.selectedServerId;
      
      const { error } = await supabaseClient.rpc('grant_acl_by_email', {
        _email: row.email,
        _scope: scope,
        _resource: resource,
        _level: newLevel
      });

      if (error) {
        if (self.message) self.message.bad(error.message || 'Update failed.');
        resetView();
      } else {
        if (self.message) self.message.good('Access level updated');
        row.level = newLevel;
        
        // Sync to waffle server if needed
        if (scope === 'server') {
          await runWaffleSyncACL(resource);
        }
        if (scope === 'dataset') {
          await runWaffleSyncACL(selectedServerId);
        }
        
        if (self.onUpdate) {
          self.onUpdate();
        } else {
          setTimeout(() => self.render(), 500);
        }
      }
    });
  }

  async revokeAccess(row) {
    if (this.message) this.message.progress('Revoking access...');
    
    // Use row's scope/resource if available (for multi-resource tables)
    const email = row.email || row;
    const scope = row.scope || this.scope;
    const resource = row.resource || this.resource;
    const selectedServerId = row.selectedServerId || this.selectedServerId;
    
    const { error } = await supabaseClient.rpc('revoke_acl_by_email', {
      _email: email,
      _scope: scope,
      _resource: resource
    });

    if (error) {
      if (this.message) this.message.bad(error.message || 'Revoke failed.');
    } else {
      if (this.message) this.message.good('Access revoked');
      
      // Sync to waffle server if needed
      if (scope === 'server') {
        await runWaffleSyncACL(resource);
      }
      if (scope === 'dataset') {
        await runWaffleSyncACL(selectedServerId);
      }
      
      if (this.onUpdate) {
        this.onUpdate();
      } else {
        setTimeout(() => this.render(), 1000);
      }
    }
  }

  async grantAccess(email, level) {
    if (this.message) this.message.progress('Granting access...');
    
    const { error } = await supabaseClient.rpc('grant_acl_by_email', {
      _email: email,
      _scope: this.scope,
      _resource: this.resource,
      _level: level
    });

    if (error) {
      if (this.message) this.message.bad(error.message || 'Grant failed.');
      return { error };
    } else {
      if (this.message) this.message.good('Access granted');
      
      // Sync to waffle server if needed
      if (this.scope === 'server') {
        await runWaffleSyncACL(this.resource);
      }
      if (this.scope === 'dataset') {
        await runWaffleSyncACL(this.selectedServerId);
      }
      
      if (this.onUpdate) {
        this.onUpdate();
      } else {
        setTimeout(() => this.render(), 1000);
      }
      
      return { ok: true };
    }
  }

  renderAddButton() {
    const self = this;
    const addItemButton = this.container.append("div")
      .attr("class", "button bigger addnew")
      .text("✚ Grant access")
      .on("click", () => {
        addItemButton.style("visibility", "hidden");
        self.renderAddForm(addItemButton);
      });
  }

  renderAddForm(addButton) {
    const self = this;
    const form = this.container.append('form').attr('class', 'form');

    form.append('div')
      .attr('class', 'form-row')
      .text("The invited user needs to be already registered in the service. Invitations are not supported yet.");
    
    const formRow = form.append('div').attr('class', 'form-row');
    
    formRow.append('div')
      .attr('class', 'form-col')
      .append('label')
      .attr('for', 'acl-email')
      .attr('class', 'label required')
      .text('Email');

    formRow.append('div')
      .attr('class', 'form-col form-grow')
      .append('input')
      .attr('type', 'email')
      .attr('id', 'acl-email')
      .attr('class', 'input')
      .attr('placeholder', 'user@example.com')
      .attr('required', true);

    const roles = ['reader', 'editor', 'owner'];
    const roleRow = formRow.append('div')
      .attr('class', 'form-col role-row')
      .append('div')
      .attr('class', 'radio-group');

    roles.forEach((r, i) => {
      const id = `role-${r}`;
      const item = roleRow.append('label').attr('class', 'radio');
      item.append('input')
        .attr('type', 'radio')
        .attr('name', 'acl-role')
        .attr('value', r)
        .attr('id', id)
        .property('checked', i === 0);
      item.append('span').attr('class', 'radio-label').text(r);
    });

    formRow.append('button')
      .attr('type', 'submit')
      .attr('class', 'button bigger addnew')
      .text('Grant access');

    if (this.showExplanations) {
      form.append('h2').text(`Explanation of ${this.scope} access levels:`);
      form.append('div').attr('class', 'form-row').text(this.getExplanation("reader"));
      form.append('div').attr('class', 'form-row').text(this.getExplanation("editor"));
      form.append('div').attr('class', 'form-row').text(this.getExplanation("owner"));
    }

    // Simple email validation
    const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

    form.on('submit', async (e) => {
      e.preventDefault();

      const email = form.select('#acl-email').property('value').trim();
      const role = form.select('input[name="acl-role"]:checked').property('value');

      if (!isEmail(email)) {
        if (self.message) self.message.bad('Invalid email.');
        return;
      }

      const { error } = await self.grantAccess(email, role);
      
      if (!error) {
        form.remove();
        if (addButton) addButton.style("visibility", "visible");
      }
    });
  }
}
